import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load available tenants on mount
  useEffect(() => {
    loadAvailableTenants();
  }, []);

  // Load current tenant from localStorage on mount
  useEffect(() => {
    const savedTenant = localStorage.getItem('currentTenant');
    if (savedTenant) {
      try {
        const tenant = JSON.parse(savedTenant);
        setCurrentTenant(tenant);
        loadUserPermissions(tenant.id);
      } catch (error) {
        console.error('Error parsing saved tenant:', error);
        localStorage.removeItem('currentTenant');
      }
    }
    setLoading(false);
  }, []);

  const loadAvailableTenants = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get('/enterprise/tenants', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Error loading tenants:', error);
      // For demo purposes, create sample tenants
      setAvailableTenants([
        {
          id: 'demo-tenant-1',
          name: 'Demo Police Department',
          tier: 'enterprise',
          max_locations: 5,
          max_users: 50,
          custom_branding: {
            logo: '/logo-police.png',
            primary_color: '#1a365d',
            company_name: 'Demo PD'
          }
        },
        {
          id: 'demo-tenant-2',
          name: 'Demo Bail Bonds Company',
          tier: 'enterprise',
          max_locations: 3,
          max_users: 25,
          custom_branding: {
            logo: '/logo-bail.png',
            primary_color: '#2d3748',
            company_name: 'Demo Bail Bonds'
          }
        }
      ]);
    }
  };

  const loadUserPermissions = async (tenantId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`/enterprise/tenant/${tenantId}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPermissions(response.data.permissions || ['read']);
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Default permissions for demo
      setUserPermissions(['read', 'write']);
    }
  };

  const switchTenant = async (tenant) => {
    try {
      setLoading(true);
      setCurrentTenant(tenant);
      localStorage.setItem('currentTenant', JSON.stringify(tenant));
      
      // Load permissions for new tenant
      await loadUserPermissions(tenant.id);
      
      // Update API base URL and headers for new tenant
      axios.defaults.headers.common['X-Tenant-ID'] = tenant.id;
      
      setError(null);
    } catch (error) {
      setError('Failed to switch tenant');
      console.error('Error switching tenant:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (requiredPermission) => {
    if (!userPermissions || userPermissions.length === 0) return false;
    
    // Admin has all permissions
    if (userPermissions.includes('admin')) return true;
    
    // Check specific permission
    return userPermissions.includes(requiredPermission);
  };

  const getTenantConfig = () => {
    if (!currentTenant) return null;
    
    return {
      ...currentTenant,
      apiBaseUrl: `/api/tenant/${currentTenant.id}`,
      branding: currentTenant.custom_branding || {}
    };
  };

  const value = {
    currentTenant,
    availableTenants,
    userPermissions,
    loading,
    error,
    switchTenant,
    hasPermission,
    getTenantConfig,
    loadAvailableTenants
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
